import React, { useCallback, useEffect, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSync } from "@fortawesome/free-solid-svg-icons"
import { COLORS } from "../../../utilities/Constants"
import {
	fetchAttentes,
	fetchMessagesAClasser,
	fetchPropositions,
	messageHorsSujet,
	rattacherMessage,
	suggestionsRattachement,
	fetchMessagesHorsSujet,
	retablirMessage,
} from "../../../store/actions/assistancesActions"
import { useAssistances } from "./AssistancesContext"
import { BandeauOmbre, Mono, dateCourte, ui } from "./assistancesUi"

// Onglet « Assistances (n) » du dispatch (GE-57, spec §11.2) : pastilles
// Propositions / En attente / Messages à classer / Rapprochements.
// Propositions : missions déjà parties en tête (pastille rouge).

const POLL_MS = 60 * 1000

const resumeOps = (p) =>
	p.operations
		.filter((o) => !o.choix || o.choix === (p.apres && p.apres.defaut))
		.slice(0, 2)
		.map((o) => o.libelle)
		.join(" · ")

// « AXA 4154859-2 · Transport · v1 → v2 » (maquette).
const refProposition = (p) => {
	const c = p.contexte || {}
	return [
		`${p.dossier.assistance} ${c.ordre_ref || p.dossier.ref_affaire}`,
		c.role_label,
		p.apres && p.apres.version > 1
			? `v${p.apres.version - 1} → v${p.apres.version}`
			: null,
	]
		.filter(Boolean)
		.join(" · ")
}

const Ligne = ({ point, titre, refTexte, badge, detail, actions }) => (
	<div style={st.ligne}>
		<span style={{ ...st.point, backgroundColor: point }} />
		<div style={{ minWidth: 0 }}>
			<div style={st.l1}>
				<span style={st.type}>{titre}</span>
				<span style={st.ref}>{refTexte}</span>
				{badge}
			</div>
			{detail && <div style={st.l2}>{detail}</div>}
		</div>
		<div style={st.actions}>{actions}</div>
	</div>
)

// Rattacher… : saisie de la réf d'affaire en ligne (même assistance).
const RattacherEnLigne = ({ message, onFait }) => {
	const dispatch = useDispatch()
	const [ouvert, setOuvert] = useState(false)
	const [ref, setRef] = useState("")
	const [enCours, setEnCours] = useState(false)
	// AS3 (CDC 16.10) : dossiers existants proposés pendant la saisie ;
	// création d'un dossier neuf seulement après confirmation explicite.
	const [suggestions, setSuggestions] = useState([])
	const [aConfirmer, setAConfirmer] = useState(null)
	const [erreur, setErreur] = useState(null)

	useEffect(() => {
		const q = ref.trim()
		if (!ouvert || q.length < 2) {
			setSuggestions([])
			return
		}
		let vivant = true
		const t = setTimeout(() => {
			dispatch(suggestionsRattachement(message.id, q)).then((r) => {
				if (vivant) setSuggestions((r && r.dossiers) || [])
			})
		}, 250)
		return () => {
			vivant = false
			clearTimeout(t)
		}
	}, [ref, ouvert, message.id, dispatch])

	const envoyer = (cible) => {
		setEnCours(true)
		setErreur(null)
		dispatch(rattacherMessage(message.id, cible)).then((res) => {
			setEnCours(false)
			if (res && res.erreur) {
				if (res.erreur.fields && res.erreur.fields.creer)
					setAConfirmer(res.erreur.message)
				else setErreur(res.erreur.message)
				return
			}
			if (res) onFait(res.id)
		})
	}

	if (!ouvert)
		return (
			<button
				type="button"
				style={ui.btn()}
				onClick={() => setOuvert(true)}
			>
				Rattacher…
			</button>
		)
	return (
		<span style={st.rattacher}>
			<span style={{ display: "inline-flex", gap: 6 }}>
				<input
					autoFocus
					value={ref}
					onChange={(e) => {
						setRef(e.target.value)
						setAConfirmer(null)
						setErreur(null)
					}}
					placeholder="Réf. d'affaire"
					style={st.saisie}
				/>
				{aConfirmer ? (
					<button
						type="button"
						style={ui.btn("primary")}
						disabled={enCours}
						onClick={() =>
							envoyer({ ref_affaire: ref.trim(), creer: true })
						}
					>
						{enCours ? (
							<FontAwesomeIcon icon={faSync} spin />
						) : (
							"Créer le dossier"
						)}
					</button>
				) : (
					<button
						type="button"
						style={ui.btn("primary")}
						disabled={!ref.trim() || enCours}
						onClick={() => envoyer({ ref_affaire: ref.trim() })}
					>
						{enCours ? (
							<FontAwesomeIcon icon={faSync} spin />
						) : (
							"OK"
						)}
					</button>
				)}
			</span>
			{suggestions.length > 0 && !aConfirmer && (
				<span style={st.suggestions}>
					{suggestions.map((d) => (
						<button
							key={d.id}
							type="button"
							style={ui.btn("ghost")}
							disabled={enCours}
							onClick={() => envoyer({ dossier_id: d.id })}
						>
							{d.ref_affaire} · {d.statut}
						</button>
					))}
				</span>
			)}
			{aConfirmer && <span style={st.confirmer}>{aConfirmer}</span>}
			{erreur && <span style={st.erreur}>{erreur}</span>}
		</span>
	)
}

// « Hors sujet » avec garde double clic (GE-59).
const BoutonHorsSujet = ({ message, onFait }) => {
	const dispatch = useDispatch()
	const [enCours, setEnCours] = useState(false)
	return (
		<button
			type="button"
			style={ui.btn("ghost")}
			disabled={enCours}
			onClick={() => {
				setEnCours(true)
				dispatch(messageHorsSujet(message.id)).then((r) => {
					setEnCours(false)
					if (r) onFait()
				})
			}}
		>
			Hors sujet
		</button>
	)
}

const DepDispatchAssistances = () => {
	const dispatch = useDispatch()
	const ctx = useAssistances()
	const [sous, setSous] = useState("props")
	const [props, setProps] = useState(null)
	const [attentes, setAttentes] = useState([])
	const [messages, setMessages] = useState([])
	const [ecartes, setEcartes] = useState([])
	const [retablissement, setRetablissement] = useState(null)
	const monte = useRef(true)
	useEffect(
		() => () => {
			monte.current = false
		},
		[],
	)

	const charger = useCallback(() => {
		dispatch(fetchPropositions()).then((d) => {
			if (monte.current) setProps(d || [])
		})
		dispatch(fetchAttentes()).then((d) => {
			if (monte.current) setAttentes(d || [])
		})
		dispatch(fetchMessagesAClasser()).then((d) => {
			if (monte.current) setMessages(d || [])
		})
		dispatch(fetchMessagesHorsSujet()).then((d) => {
			if (monte.current) setEcartes(d || [])
		})
	}, [dispatch])

	const retablir = (m) => {
		setRetablissement(m.id)
		dispatch(retablirMessage(m.id)).then((r) => {
			if (!monte.current) return
			setRetablissement(null)
			if (r) {
				charger()
				setSous("classer")
			}
		})
	}

	useEffect(() => {
		charger()
		const t = setInterval(charger, POLL_MS)
		return () => clearInterval(t)
	}, [charger, ctx.revision])

	const propositions = (props || []).filter((p) => p.type !== "rapprochement")
	const rapprochements = (props || []).filter(
		(p) => p.type === "rapprochement",
	)
	const pastilles = [
		{ cle: "props", label: "Propositions", n: propositions.length },
		{ cle: "attente", label: "En attente", n: attentes.length },
		{ cle: "classer", label: "Messages à classer", n: messages.length },
		{ cle: "rappro", label: "Rapprochements", n: rapprochements.length },
		{ cle: "ecartes", label: "Hors sujet", n: ecartes.length },
	]
	const ouvrir = (p) => ctx.ouvrirProposition(p)

	return (
		<div>
			{ctx.ombre && <BandeauOmbre />}
			<div style={st.pastilles} role="tablist">
				{pastilles.map((p) => (
					<button
						key={`sous_${p.cle}`}
						type="button"
						style={st.pastille(sous === p.cle)}
						onClick={() => setSous(p.cle)}
					>
						{p.label} <span style={{ fontWeight: 700 }}>{p.n}</span>
					</button>
				))}
			</div>

			{props === null && (
				<div style={{ padding: 24, textAlign: "center" }}>
					<FontAwesomeIcon icon={faSync} spin />
				</div>
			)}

			{props !== null && sous === "props" && (
				<div style={st.liste}>
					{propositions.length === 0 && (
						<Vide texte="Aucune proposition ouverte." />
					)}
					{propositions.map((p) => (
						<Ligne
							key={`p_${p.id}`}
							point={
								p.mission_partie
									? COLORS.danger
									: COLORS.warning
							}
							titre={p.type_label}
							refTexte={refProposition(p)}
							badge={
								<>
									{p.mission_partie && (
										<span style={st.badgeParti}>
											{p.mission_partie_libelle ||
												"mission partie"}
										</span>
									)}
									{p.simulee && (
										<span style={st.badgeSimulee}>
											simulée
										</span>
									)}
								</>
							}
							detail={[
								resumeOps(p),
								`calculée ${dateCourte(p.cree_le)}`,
							]
								.filter(Boolean)
								.join(" · ")}
							actions={
								<>
									<button
										type="button"
										style={ui.btn()}
										onClick={() =>
											ctx.ouvrirDossier(p.dossier.id)
										}
									>
										Dossier
									</button>
									<button
										type="button"
										style={ui.btn("primary")}
										onClick={() => ouvrir(p)}
									>
										Ouvrir
									</button>
								</>
							}
						/>
					))}
				</div>
			)}

			{props !== null && sous === "attente" && (
				<div style={st.liste}>
					{attentes.length === 0 && (
						<Vide texte="Aucun ordre en délai de grâce." />
					)}
					{attentes.map((a) => (
						<Ligne
							key={`a_${a.ordre.id}`}
							point={COLORS.warning}
							titre="Transport depuis notre dépôt"
							refTexte={`${a.dossier.assistance} ${a.dossier.ref_affaire}${
								a.ordre.ref_ordre
									? ` · ${a.ordre.ref_ordre}`
									: ""
							} · reçu ${dateCourte(a.ordre.recu_le)}`}
							detail={
								<>
									En attente de l'ordre de dépannage —{" "}
									<b>
										encore{" "}
										{Math.max(
											1,
											Math.ceil(
												(new Date(
													a.fin_grace,
												).getTime() -
													Date.now()) /
													60000,
											),
										)}{" "}
										min
									</b>
									, puis question au bureau
								</>
							}
							actions={
								<button
									type="button"
									style={ui.btn()}
									onClick={() =>
										ctx.ouvrirDossier(a.dossier.id)
									}
								>
									Dossier
								</button>
							}
						/>
					))}
				</div>
			)}

			{props !== null && sous === "classer" && (
				<div style={st.liste}>
					{messages.length === 0 && (
						<Vide texte="Aucun message à classer." />
					)}
					{messages.map((m) => (
						<Ligne
							key={`m_${m.id}`}
							point={COLORS.borderStrong}
							titre={`« ${m.sujet || "(sans sujet)"} »`}
							refTexte={`${m.assistance} · reçu ${dateCourte(m.recu_le)}${
								m.role ? ` · lu : ${m.role}` : ""
							} · ${m.motif || "aucune référence lisible"}`}
							actions={
								<>
									<button
										type="button"
										style={ui.btn()}
										onClick={() => ctx.ouvrirMessage(m.id)}
									>
										Voir l'e-mail
									</button>
									<RattacherEnLigne
										message={m}
										onFait={(dossierId) => {
											ctx.actualiser()
											ctx.ouvrirDossier(dossierId)
										}}
									/>
									<BoutonHorsSujet
										message={m}
										onFait={() => ctx.actualiser()}
									/>
								</>
							}
						/>
					))}
				</div>
			)}

			{props !== null && sous === "ecartes" && (
				<div style={st.liste}>
					{ecartes.length === 0 && (
						<Vide texte="Aucun e-mail écarté." />
					)}
					{ecartes.map((m) => (
						<Ligne
							key={`e_${m.id}`}
							point={COLORS.borderStrong}
							titre={`« ${m.sujet || "(sans sujet)"} »`}
							refTexte={`${m.assistance} · reçu ${dateCourte(m.recu_le)} · écarté${
								m.ecarte_le
									? ` le ${dateCourte(m.ecarte_le)}`
									: ""
							}${
								m.ecarte_par === "Lecture automatique"
									? " automatiquement (lecture)"
									: m.ecarte_par
										? ` par ${m.ecarte_par}`
										: ""
							}${
								m.purge_le
									? ` · copie purgée le ${dateCourte(m.purge_le)}`
									: ""
							}`}
							actions={
								<>
									<button
										type="button"
										style={{
											...ui.btn(),
											...(m.purge_le ? st.inactif : {}),
										}}
										disabled={!!m.purge_le}
										title={
											m.purge_le
												? "Copie purgée (30 jours après réception)"
												: undefined
										}
										onClick={() => ctx.ouvrirMessage(m.id)}
									>
										Voir l'e-mail
									</button>
									<button
										type="button"
										style={{
											...ui.btn("primary"),
											...(m.purge_le ? st.inactif : {}),
										}}
										disabled={
											!!m.purge_le ||
											retablissement === m.id
										}
										title={
											m.purge_le
												? "Copie purgée : rétablissement impossible ici (l'original reste dans la boîte e-mail)"
												: "Remettre l'e-mail « à classer »"
										}
										onClick={() => retablir(m)}
									>
										{retablissement === m.id ? (
											<FontAwesomeIcon
												icon={faSync}
												spin
											/>
										) : (
											"Rétablir"
										)}
									</button>
								</>
							}
						/>
					))}
				</div>
			)}

			{props !== null && sous === "rappro" && (
				<div style={st.liste}>
					{rapprochements.length === 0 && (
						<Vide texte="Aucun rapprochement suggéré." />
					)}
					{rapprochements.map((p) => (
						<Ligne
							key={`r_${p.id}`}
							point={COLORS.accent}
							titre="Même véhicule ?"
							refTexte={`${p.dossier.assistance} ${p.dossier.ref_affaire} ↔ ${
								p.apres.ref_affaire
									? `${p.dossier.assistance} ${p.apres.ref_affaire}`
									: `M${p.apres.mission}`
							}`}
							detail={
								<>
									Plaque{" "}
									<Mono>
										{p.apres.plaques || "identique"}
									</Mono>{" "}
									· jamais automatique
								</>
							}
							actions={
								<>
									<button
										type="button"
										style={ui.btn()}
										onClick={() =>
											ctx.ouvrirDossier(p.dossier.id)
										}
									>
										Dossier
									</button>
									<button
										type="button"
										style={ui.btn("primary")}
										onClick={() => ouvrir(p)}
									>
										{p.apres.ref_affaire
											? "Fusionner les dossiers…"
											: "Lier…"}
									</button>
								</>
							}
						/>
					))}
				</div>
			)}
		</div>
	)
}

const Vide = ({ texte }) => (
	<div style={{ color: COLORS.textMuted, fontSize: 13, padding: "8px 2px" }}>
		{texte}
	</div>
)

const st = {
	pastilles: { display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 },
	pastille: (on) => ({
		border: `1px solid ${on ? COLORS.accent : COLORS.border}`,
		backgroundColor: on ? COLORS.accentLight : COLORS.surface,
		color: on ? COLORS.accent : COLORS.textMuted,
		borderRadius: 99,
		padding: "6px 14px",
		fontSize: 13,
		fontWeight: 500,
		cursor: "pointer",
		display: "inline-flex",
		gap: 8,
		alignItems: "center",
		fontFamily: "inherit",
	}),
	liste: { display: "flex", flexDirection: "column", gap: 10, maxWidth: 980 },
	ligne: {
		backgroundColor: COLORS.surface,
		border: `1px solid ${COLORS.border}`,
		borderRadius: 12,
		padding: "14px 16px",
		display: "grid",
		gridTemplateColumns: "10px minmax(0, 1fr) auto",
		gap: 12,
		alignItems: "center",
	},
	point: { width: 10, height: 10, borderRadius: "50%" },
	l1: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" },
	type: { fontWeight: 700, fontSize: 14 },
	ref: { fontSize: 12.5, color: COLORS.textMuted },
	l2: { fontSize: 13, color: COLORS.textMuted, marginTop: 3 },
	actions: {
		display: "flex",
		gap: 8,
		flexWrap: "wrap",
		justifyContent: "flex-end",
	},
	badgeParti: {
		padding: "2px 10px",
		borderRadius: 99,
		fontSize: 11,
		fontWeight: 700,
		color: "#166534",
		backgroundColor: "#DCFCE7",
	},
	badgeSimulee: {
		padding: "2px 10px",
		borderRadius: 99,
		fontSize: 11,
		fontWeight: 700,
		color: "#92400E",
		backgroundColor: COLORS.warningLight,
	},
	rattacher: {
		display: "inline-flex",
		flexDirection: "column",
		gap: 6,
		alignItems: "flex-start",
	},
	suggestions: { display: "inline-flex", flexWrap: "wrap", gap: 4 },
	inactif: { opacity: 0.4, cursor: "not-allowed" },
	confirmer: { fontSize: 12, color: COLORS.warning, maxWidth: 360 },
	erreur: { fontSize: 12, color: COLORS.danger, maxWidth: 360 },
	saisie: {
		width: 140,
		border: `1px solid ${COLORS.border}`,
		borderRadius: 8,
		padding: "6px 10px",
		fontFamily: "inherit",
		fontSize: 13,
	},
}

export default DepDispatchAssistances
